"use client"

import React from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, Store } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getAuthRegistry } from "@/lib/auth/AuthRegistry";
import { configureApi } from "@/lib/apiClientRuntime";

/**
 * Multi-salon switcher (docs/codebase/multi-salon/ §6 in the backend repo).
 *
 * Lists the login's salons from the identity service and re-scopes the
 * session on pick: /switch-company mints a token for the chosen salon,
 * we store it, point the API client at the salon's region and reload —
 * every query in the app is company-scoped, so a full reload is the only
 * safe switch (decision §7.4).
 *
 * Renders nothing for single-salon accounts.
 */

const IDENTITY_BASE = "https://id.boona.app";

type Company = {
  id: number;
  title: string;
  region: string;
  is_primary: boolean;
};

type CompaniesResponse = {
  current_company_id: number;
  companies: Company[];
};

async function fetchCompanies(): Promise<CompaniesResponse | null> {
  const { accessToken } = getAuthRegistry().getTokens();
  if (!accessToken) return null;
  const response = await fetch(`${IDENTITY_BASE}/account/companies`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: "include",
  });
  if (!response.ok) return null;
  return response.json();
}

export function SalonSwitcher() {
  const { t } = useTranslation();
  const [switching, setSwitching] = React.useState(false);

  const { data } = useQuery({
    queryKey: ["account-companies"],
    queryFn: fetchCompanies,
    staleTime: 10 * 60 * 1000,
    retry: 1,
  });

  if (!data || data.companies.length < 2) return null;

  const current =
    data.companies.find((c) => c.id === data.current_company_id) ??
    data.companies[0];

  async function switchTo(company: Company) {
    if (switching || company.id === data!.current_company_id) return;
    setSwitching(true);
    try {
      const { accessToken } = getAuthRegistry().getTokens();
      const body = new URLSearchParams({ company_id: String(company.id) });
      const response = await fetch(`${IDENTITY_BASE}/switch-company`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        credentials: "include", // refreshes the SSO cookie for the dashboard too
        body,
      });
      if (!response.ok) throw new Error(`switch failed: ${response.status}`);
      const payload = await response.json();

      const registry = getAuthRegistry();
      const user = registry.getUser();
      const region =
        payload.region && payload.region !== "default" ? payload.region : null;
      if (region) configureApi(`https://api-${region}.boona.app`);
      registry.setAuth(
        user ?? { id: "sso", email: "", name: company.title },
        { accessToken: payload.access_token, refreshToken: null },
        region,
      );
      window.location.assign("/"); // full reload: all caches are company-scoped
    } catch (error) {
      console.error("[SalonSwitcher] switch failed", error);
      setSwitching(false);
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          disabled={switching}
          className="inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
          aria-label={t("salon_switcher.label", "Switch salon")}
          data-testid="salon-switcher"
        >
          <Store size={15} />
          <span className="max-w-36 truncate">{current.title}</span>
          <ChevronDown size={14} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {data.companies.map((company) => (
          <DropdownMenuItem
            key={company.id}
            disabled={company.id === data.current_company_id}
            onSelect={() => switchTo(company)}
          >
            {company.title}
            {company.id === data.current_company_id
              ? ` · ${t("salon_switcher.current", "current")}`
              : ""}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
